package com.meistercharts.charts.pv.modulePlanning

import com.meistercharts.algorithms.layers.addClearBackground
import com.meistercharts.algorithms.layers.addShowLoadingOnMissingResources
import com.meistercharts.algorithms.layers.visibleIf
import com.meistercharts.canvas.BindContentAreaSize2Property
import com.meistercharts.canvas.ConfigurationDsl
import com.meistercharts.canvas.MeisterchartBuilder
import com.meistercharts.canvas.resize.addResizeByHandlesLayer
import com.meistercharts.charts.ChartGestalt
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningLayer
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningModel
import com.meistercharts.charts.pv.roofPlanning.PvRoofSampleModels
import com.meistercharts.charts.pv.roofPlanning.RoofBackgroundLayer
import com.meistercharts.model.Insets
import com.meistercharts.resize.ResetToDefaultsOnWindowResize
import com.meistercharts.zoom.FittingWithMarginAspectRatio
import it.neckar.geometry.AxisOrientationX
import it.neckar.geometry.AxisOrientationY

/**
 * Gestalt for roof planning
 */
class PvModulePlanningGestalt(
  val configuration: Configuration,
  additionalConfiguration: Configuration.() -> Unit = {},
) : ChartGestalt {

  constructor(
    model: PvRoofPlanningModel = PvRoofSampleModels.realistic(),
    additionalConfiguration: Configuration.() -> Unit = {},
  ) : this(Configuration(model), additionalConfiguration)

  init {
    configuration.additionalConfiguration()
  }

  val roofBackgroundLayer: RoofBackgroundLayer = RoofBackgroundLayer({ configuration.model.roofSize })
  val pvRoofPlanningLayer: PvRoofPlanningLayer = PvRoofPlanningLayer(configuration.model)

  val pvModulePlanningLayer: PvModulePlanningLayer = PvModulePlanningLayer(pvRoofPlanningLayer = pvRoofPlanningLayer)

  var mode: PvRoofPlanningLayer.Mode
    get() = pvRoofPlanningLayer.configuration.mode
    set(value) {
      roofBackgroundLayer.configuration.mode = value
      //The gate around pvModulePlanningLayer reads the mode from here
      pvRoofPlanningLayer.configuration.mode = value
    }

  override fun configure(meisterChartBuilder: MeisterchartBuilder) {
    with(meisterChartBuilder) {
      enableZoomAndTranslation = false
      contentAreaSizingStrategy = BindContentAreaSize2Property(configuration.model.roofSizeProperty)

      zoomAndTranslationDefaults {
        FittingWithMarginAspectRatio {
          Insets.of(20.0)
        }
      }
      configure {
        chartSupport.windowResizeBehavior = ResetToDefaultsOnWindowResize
      }

      configure {
        chartSupport.rootChartState.axisOrientationY = AxisOrientationY.OriginAtBottom //everything is measured from the bottom
        chartSupport.rootChartState.axisOrientationX = AxisOrientationX.OriginAtLeft //everything is measured from the left

        layers.addClearBackground()
        layers.addLayer(roofBackgroundLayer)
        layers.addLayer(pvRoofPlanningLayer)
        //The planning layer's only mode question - invisible it neither lays out nor answers events, so its resize handles expire with its last registration
        layers.addLayer(pvModulePlanningLayer.visibleIf { pvModulePlanningLayer.configuration.planningEnabled })
        layers.addResizeByHandlesLayer()
        layers.addShowLoadingOnMissingResources()
      }
    }
  }

  @ConfigurationDsl
  class Configuration(val model: PvRoofPlanningModel = PvRoofSampleModels.realistic())
}
